import {ReactNode} from 'react';

import {
  ThemeProviderWithoutClassic as ConvUIThemeProviderWithoutClassic,
  MessagePartComponentProvider,
  type MessagePartComponentRegistry,
  type Theme,
} from '@stripe-internal/conversations-ui';
import {InlineDataComponentRegistry} from '@stripe-internal/inline-data';
import {Observability} from '@stripe-internal/conversations-client';

import {AppCloseHandler, AppConfig, AppProfile} from '../../core/types';
import {AppMessagePartHandlers} from './MessagePartHandlers';
import {AppProvider} from '../../core/providers/AppProvider';
import {ConversationsUIFlagsProvider} from './ConversationsUIFlagsProvider';
import {InlineDataProvider} from '../../core/providers/InlineDataProvider';
import {ParentMessageBus} from '../../core/lib/parentMessageBus/ParentMessageBus';

type Props = {
  config: AppConfig;
  profile: AppProfile;
  clientFetch?: typeof fetch;
  parentMessageBus?: ParentMessageBus;
  inlineDataComponents?: InlineDataComponentRegistry;
  messagePartComponents?: MessagePartComponentRegistry;
  children: ReactNode;
  onClose?: AppCloseHandler;
  observability?: Observability;
  theme?: Theme;
  themeOverrides?: Partial<Theme>;
};

export function StripeAssistantProviders({
  config,
  profile,
  clientFetch,
  parentMessageBus,
  inlineDataComponents,
  messagePartComponents,
  onClose,
  observability,
  children,
  theme,
  themeOverrides,
}: Props) {
  return (
    <ConvUIThemeProviderWithoutClassic value={theme} overrides={themeOverrides}>
      <AppProvider
        config={config}
        profile={profile}
        clientFetch={clientFetch}
        parentMessageBus={parentMessageBus}
        onClose={onClose}
        observability={observability}
      >
        <ConversationsUIFlagsProvider>
          <AppMessagePartHandlers>
            <MessagePartComponentProvider value={messagePartComponents}>
              <InlineDataProvider components={inlineDataComponents}>
                {children}
              </InlineDataProvider>
            </MessagePartComponentProvider>
          </AppMessagePartHandlers>
        </ConversationsUIFlagsProvider>
      </AppProvider>
    </ConvUIThemeProviderWithoutClassic>
  );
}
